<template>
  <div @click="selectedItem = null">
    <breadcrumbs home="Bottling">
      <template v-slot:right>
        <v-menu offset-y v-if="company.permissions.includes('config.write')" :close-on-content-click="false" nudge-bottom="5" left max-width="500">
          <template v-slot:activator="{ on }">
            <v-icon small v-on="on">mdi-tune-variant</v-icon>
          </template>
          <production-bottling-preferences @update="preferences=$event"/>
        </v-menu>
      </template>
    </breadcrumbs>

      <v-card flat>
        <v-form v-model="valid" ref="form" :disabled="!edit">
          <v-container fluid class="pt-6 px-6 purtrak-scrollable">

            <v-overlay absolute :opacity="0.15" style="calc(100vh - 158px)"
                      v-if="(!products.length || !allTanks.length) && !loading && !bottlingId">
              <div style="position:relative;top:-300px;width:400px" class="d-inline-block purple white--text text-body-1 pa-5">
                Before you bottle, you must have
                <template v-if="products.length===0">
                  products defined.
                  <div class="mt-8 float-right">
                    <v-btn :to="{name:'InventoryProducts'}" color="primary" small>Products</v-btn>
                  </div>
                </template>
                <template v-else-if="allTanks.length===0">
                  tanks defined.
                  <div class="mt-8 float-right">
                    <v-btn :to="{name:'EquipmentTanks'}" color="primary" small>Tanks</v-btn>
                  </div>
                </template>
              </div>
            </v-overlay>

            <v-row dense>
              <v-col cols="6" sm="4" md="3">
                <date-picker v-model="bottling.date" @change="refresh();refreshLot()"/>
              </v-col>
              <v-col cols="6" sm="3" lg="2" v-if="preferences.bottleDuration">
                <duration-picker label="Duration" v-model="bottling.duration" dense/>
              </v-col>
              <v-col cols="4" sm="2" v-if="preferences.bottleIncomplete">
                <v-checkbox label="Completed" v-model="bottling.completed" dense class="mt-1"  @change="validate()"/>
              </v-col>
              <v-col cols="4" sm="3" lg="2" :offset-md="3+(preferences.bottleDuration?0:2)+(preferences.bottleIncomplete?0:2)">
                <v-text-field label="#" v-model="bottling.number" :rules="[fieldRules.required,fieldRules.maxLength(20)]" outlined dense :counter="20"/>
              </v-col>
            </v-row>
            <v-row dense v-if="sites.length>1">
              <v-col cols="12" sm="6" md="4">
                <v-autocomplete outlined dense label="Site" v-model="bottling.siteId" :items="sites" item-value="id" item-text="name"/>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="6" md="4">
                <multi-level-select v-if="!!productCategories" label="Product to bottle"  :menuData="productCategories" :second.sync="bottling.productId" outlined
                                    :hint="hint" persistent-hint @change="selectProduct"/>
                <v-autocomplete v-else v-model="bottling.productId" outlined dense label="Product to bottle" :rules="[requiredAlways]"
                                @change="selectProduct"
                                :items="products" item-text="name" item-value="id"
                                :hint="hint" persistent-hint/>
              </v-col>
              <v-col cols="6" sm="2" v-if="product.alcohol && product.alcohol.type!=='NonExcise' && preferences.bottleDutyPaidOption">
                <v-tooltip bottom  max-width="300" nudge-top="20" open-delay="1000">
                  <template v-slot:activator="{on}">
                    <v-checkbox v-model="bottling.dutyPaid" v-on="on" dense class="mt-1">
                      <span slot="label" v-on="on">To duty-paid</span>
                    </v-checkbox>
                  </template>
                  If selected, the finished product will be in duty-paid inventory, not in-bond under your Excise Warehouse License.
                  This means excise will be payable for the produced product.
                </v-tooltip>
              </v-col>
            </v-row>
            <v-row v-if="product.description" class="mb-4">
              <v-col cols="12" lg="8" class="primary--text">
                <em>{{product.description}}</em>
              </v-col>
            </v-row>
            <v-row denkse cklass="mt-2">
              <v-col cols="12" sm="6" md="4">
                <tank-select v-model="bottling.tankId" :tanks="tanks" :required="this.bottling.completed" :hint="bottlingId===undefined"
                            :highlight="tank => bottlingId === undefined &&
                                                product.alcohol &&
                                                tank.contents.alcoholId === product.alcohol.id &&
                                                (!preferences.bottleProof || tank.contents.proofed===product.strength || product.allowVariableStrength)"
                            outlined label="From tank"
                            @change="changeTank"/>
              </v-col>
              <v-col cols="6" sm="3" md="3" v-if="!preferences.bottleProof && product.alcohol && product.alcohol.type!=='NonExcise'">
                <alcohol-input label="Tank strength" strength-only :strength.sync="bottling.strength" @change="setEstimate"/>
              </v-col>
            </v-row>
            <div v-if="product.alcohol && product.alcohol.type!=='NonExcise' && product.allowVariableStrength && bottling.strength"
                 class="mb-4 primary--text">
              Product strength will be set to {{bottling.strength | number(2)}}% alc./vol. Your product should be appropriately labelled.
            </div>
            <cases-bottles :cases.sync="bottling.cases" :bottles.sync="bottling.bottles" :product="product" :availableCount="estimate" avaiablePrefix="Estimate"/>
            <v-row>
              <v-col cols="6" sm="2">
                <v-text-field v-model="bottling.lot"
                              label="Product lot" outlined dense
                              :rules="[required,fieldRules.maxLength(20)]" :counter="20"/>
              </v-col>
            </v-row>

            <v-row class="my-2">
              <v-col cols="12" lg="8">
                <outlined-block title="Bill of materials" :disabled="!edit" v-model="bottling.bom" :rules="[bomCheck]">
                  <v-data-table width="100%" calculate-widths disable-filtering disable-pagination disable-sort hide-default-footer
                                :items="bottling.bom" item-key="uniqueId"
                                :no-data-text="bottling.productId ? product.name + ' does not have a bill of materials' : 'No items specified'"
                                :headers="[{text:'',value:'add',width:'15%',align:'center',cellClass:'pa-0',divider:true},
                                          {text:'Item',value:'item',width:'35%',divider:true,class:'px-2',cellClass:'py-2 px-1 pt'},
                                          {text:'Quantity',value:'quantity',width:'25%',divider:true,class:'px-2',cellClass:'py-2 px-1 pt',align:'right'},
                                          {text:'Lot',value:'lot',width:'25%',divider:true,class:'px-2',cellClass:'py-2 px-1 pt'}]"  class="no-sort"
                                :item-class="item => item.uniqueId=== selectedItem ? ($vuetify.theme.dark ? 'grey darken-1' : 'grey lighten-4') : ''"
                                @click.native.stop=""
                                @click:row="item => {selectedItem = item.uniqueId; $nextTick(() => this.$refs.form.validate())}"
                                ref="bomTableBody">
                    <template v-slot:item.add="{item,index}">
                      <v-icon class="handle mt-n2" :disabled="!edit">mdi-drag</v-icon>
                      <v-btn class="mt-n2" icon @click.stop="deleteItem(index)" x-small :disabled="!edit"><v-icon>mdi-delete</v-icon></v-btn>
                    </template>
                    <template v-slot:item.item="{item}">
                      <template v-if="selectedItem === item.uniqueId">
                        <multi-level-select v-if="itemCategories.length" :menuData="eligibleItems" :second.sync="item.id" @change="changeBOMItem(item, false)" outlined label="Item"/>
                        <v-autocomplete v-else :items="items" item-text="name" item-value="id" v-model="item.id" dense @change="changeBOMItem(item, false)" :rules="[required]" class="text-body-2" outlined label="Item"/>
                      </template>
                      <div v-else class="ml-1" :class="{'error--text':!item.id}">
                        {{bomItem(item.id).name}}
                      </div>
                    </template>
                    <template v-slot:item.quantity="{item}">
                      <decimal-number-input v-if="selectedItem === item.uniqueId" label="Quantity" v-model="item.quantity" prefix=""
                            :suffix="bomItem(item.id).trackUnits" :precision="bomItem(item.id).precision" class="text-body-2" @change="validate()"
                            :hint="bottlingId?'':bomItem(item.id).quantity" persistent-hint/>
                      <div v-else class="mr-1" :class="{'error--text':item.quantity < 0}">
                        {{item.quantity | number(bomItem(item.id).precision)}} {{bomItem(item.id).trackUnits}}
                      </div>
                    </template>
                    <template v-slot:item.lot="{item}">
                      <template v-if="bomItem(item.id).lotTracking">
                        <v-combobox v-if="selectedItem === item.uniqueId" dense outlined label="Lot" v-model="item.lot" :rules="[required,fieldRules.maxLength(20)]" :items="currentLots(item.id)"
                                    class="text-body-2" no-data-text="No inventory" @change="changeItemLot(item)"/>
                        <div v-else class="ml-1" :class="{'error--text':required(item.lot) !== true}">
                          {{item.lot || 'Required'}}
                        </div>
                      </template>
                      <div v-else>n/a</div>
                    </template>
                    <template v-slot:footer="{widths}">
                      <v-divider/>
                      <div @click="selectedItem = null" style="width:100%" class="py-2 px-0">
                        <div :style="'width:'+widths[0]+'px'" class="text-center">
                          <v-btn :disabled="!edit || items.length===0" fab color="primary" x-small @click.stop="addItem"><v-icon small>mdi-plus</v-icon></v-btn>
                        </div>
                      </div>
                    </template>
                  </v-data-table>
                </outlined-block>
              </v-col>
            </v-row>

            <v-row dense>
              <v-col>
                <v-checkbox v-model="bottling.reduceInventory" label="Reduce remaining tank volume to zero" class="mt-1 mb-5" dense
                            hint="If selected, the full dollar value of the tank will be transferred to the product and the remaining volume will be logged as a production loss"
                            persistent-hint/>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" lg="8">
                <v-textarea label="Comments" rows="4"
                           v-model="bottling.comments" outlined dense :rules="[fieldRules.maxLength(500)]" counter="500"/>
              </v-col>
            </v-row>
          </v-container>
        </v-form>

        <v-divider/>
        <v-card-actions class="px-6">
        <template v-if="bottlingId && company.permissions.includes('production.write')">
          <v-switch v-if="bottling.date > company.excisePeriodEnd || edit" :disabled="dirty"
                    v-model="edit" label="Edit" hide-details class="mt-0"/>
          <div v-else class="grey--text">Cannot edit since excise filed to period end {{company.excisePeriodEnd | formatDate}}</div>
        </template>
        <v-spacer/>
        <v-btn :plain="edit" color="primary" @click="cancel">{{edit?'Cancel':'Done'}}</v-btn>
        <v-btn v-if="edit" color="primary" :loading="saving" @click="save">Save</v-btn>
        </v-card-actions>
      </v-card>

    <confirm-leave v-model="next"/>
  </div>
</template>

<script>
import {format} from 'date-fns';
import sortableJS from 'sortablejs';
import confirmLeave from '@/mixins/confirmLeave'
import TankSelect from '@/components/TankSelect'
import CasesBottles from '@/components/CasesBottles'
import ProductionBottlingPreferences from '@/views/production/bottling/ProductionBottlingPreferences'
import createHierarchyList from '@/mixins/createHierarchyList'

export default {
  name: 'ProductionBottlingEdit',
  components: {TankSelect,ProductionBottlingPreferences,CasesBottles},
  mixins: [confirmLeave,createHierarchyList],
  props: {
    bottlingId: {type:String, required:false},
    productId: {type:String, required:false},
    cases: {type:Number, required:false, default:0},
    activityId: {type:String, required:false}
  },
  data () {
    return {
      edit: false,
      preferences: {},
      bottling: {date:format(new Date(), 'yyyy-MM-dd HH:mm:ss'),number:'',tankId:'',productId:'',reduceInventory:false, dutyPaid: false,
                 siteId:this.$store.getters.sites(true, {B265:true,B266:true})[0].id, strength:0,
                 completed:true,duration:0,comments:'',cases:this.cases,bottles:0,lot:'',bom:[],activityId:this.activityId},
      products: [],
      productCategories: null,
      allTanks: [],
      items: [],
      itemCategories: [],
      estimate: 0,

      dirty: false,
      saving: false,
      loading: true,
      selectedItem: null,
      valid: true,
      requiredAlways: v => !!v || 'Required',
      required: v => !this.bottling.completed || !!v || 'Required',
      sortable: null
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    sites() {
      return this.$store.getters.sites(!this.bottlingId, {B265:true,B266:true});
    },
    tanks() {
      return this.allTanks.filter(tank => tank.site.id === this.bottling.siteId);
    },
    product() {
      return this.products.find(product => product.id === this.bottling.productId) || {};
    },
    hint() {
      const at = this.product.alcohol?.type !== 'NonExcise' ? (this.preferences.bottleProof?' proofed to ':' at ') + this.product.strength + '%' : '';
      return `Requires ${this.product.alcohol?.name} ${at}`;
    },
    eligibleItems() {
      const categories = this.createHierarchyList(this.itemCategories);
      for (const category of categories) {
        const sub = this.items.filter(item => item.category?.id === category.id);
        if (sub.length)
          category.menu = sub;
        else
          category.disabled = true;
      }
//      const items = [...categories.filter(category => !category.disabled)];
      const items = categories;
      const leftOver = this.items.filter(item => !item.category?.id);
      if (leftOver.length)
        items.push({divider:true},{name:'Other items',id:'x',menu:leftOver});
      return items;
    }
  },
  async mounted() {
    const productCategories = [];
    await Promise.all([
      this.$lazyListLoad(this.itemCategories, 'categories2', 'id,name,parent{id}', 100, [], [{name:'type',value:'Item'}]),
      this.$lazyListLoad(productCategories, 'categories2', 'id,name,parent{id}', 100, [], [{name:'type',value:'Product'}]),
      this.$lazyListLoad(this.products, 'products2', 'id,name,containerType,strength,caseSize,bottleSize,alcohol{id,type,name},caseSize,category{id},allowVariableStrength', 100, !this.bottlingId ? [{field:'active', comparator:'eq', value:'true'},{field:'mixedPack',comparator:'eq',value:'false'}] : []),
      this.$lazyListLoad(this.items, 'items2', 'id,name,trackUnits,lotTracking,type,precision,category{id}', 100, !this.bottlingId ? [{field:'active', comparator:'eq', value:'true'}] : [])
    ]);

    const results = await this.$apiCall(`query ProductionBottlingEdit($edit:Boolean!,$id:ID!) {
                                           bottlingPreferences {
                                             bottleDuration,bottleIncomplete,bottleProof,bottleDutyPaidOption,bottleDutyPaid,lotFromSpirit
                                           }
                                           bottling(id:$id) @include(if:$edit) {
                                             id,date,number,created,siteId,tankId,productId,dutyPaid,completed,duration,comments,bottles,lot,reduceInventory,strength
                                             bom{type,id,quantity,lot}
                                           }
                                         }`,
                                         {edit:this.bottlingId!==undefined,
                                          id:this.bottlingId || ''});
    if (results.error) {
      this.$nextTick(() => this.dirty = false);
      this.$router.back();
      return;
    }
    this.preferences = results.data.bottlingPreferences;

    if (productCategories.length) {
      const categories = this.createHierarchyList(productCategories);
      for (const category of categories) {
        const sub = this.products.filter(product => product.category?.id === category.id);
        if (sub.length)
          category.menu = sub;
        else
          category.disabled = true;
      }
//      this.productCategories = [...categories.filter(category => !category.disabled)];
      this.productCategories = categories;
      const leftOver = this.products.filter(product => !product.category?.id);
      if (leftOver.length)
        this.productCategories.push({divider:true},{name:'Other products',id:'x',menu:leftOver});
    }

    if (this.bottlingId) {
      await this.$lazyListLoad(this.allTanks, 'tanks2', 'id,name,site{id}', 100);
      let uniqueId = Date.now();
      results.data.bottling.bom.forEach(item => item.uniqueId = uniqueId++);
      Object.assign(this.bottling, results.data.bottling, {cases:0});
      if (!this.bottling.completed)
        this.bottling.duration = this.$roundNumber(Date.now()/60000 - this.bottling.created, 0);
      await this.selectProduct();
      this.bottling.cases = Math.floor(this.bottling.bottles / this.product.caseSize);
      this.bottling.bottles -= this.bottling.cases * this.product.caseSize;
      if (!this.bottling.completed)
        this.edit = true;
    }
    else if (this.products.length) {
      const results = await this.$apiCall(`query ProductionBottlingEdit {
                                              bottlingNextNumber
                                            }`);
      if (!results.error)
        this.bottling.number = results.data.bottlingNextNumber;
      await this.refresh();
      this.bottling.productId = this.productId || '';
      this.bottling.dutyPaid = this.preferences.bottleDutyPaid;
      this.edit = true;
      await this.selectProduct();
    }

    await this.refreshLot();
    this.$nextTick(() => {
      this.dirty = false
      this.validate();
    });
    this.loading = false;

    const _self = this;
    this.sortable = sortableJS.create(this.$refs.bomTableBody.$el.querySelector('tbody'), {
      handle: '.handle',
      onEnd({newIndex, oldIndex}) {
        const rowSelected = _self.bottling.bom.splice(oldIndex, 1)[0];
        _self.bottling.bom.splice(newIndex, 0, rowSelected);
      }
    })
  },
  beforeDestroy() {
    this.sortable.destroy();
  },
  watch: {
    bottling: {
      handler() {
        this.dirty = true;
      },
      immediate: true,
      deep: true
    },
    'bottling.cases'() {
      this.bom();
    },
    'bottling.bottles'() {
      this.bom();
    }
  },

  methods: {
    setEstimate() {
      if (this.bottlingId)    // Not for existing bottling
        return;
      if (this.bottling.productId && this.bottling.tankId) {
        const tank = this.tanks.find(tank => tank.id === this.bottling.tankId);
        const strength = !this.preferences.bottleProof ? this.bottling.strength : tank.contents.proofed;
        if (strength && tank.contents) {
          this.estimate = Math.floor((tank.contents.volume * 100 / strength) / (this.product.bottleSize / 1000));
        }
      }
    },
    changeTank() {
      this.setEstimate();
      const tank = this.tanks.find(tank => tank.id === this.bottling.tankId);
      if (this.preferences.lotFromSpirit)
        this.bottling.lot = tank?.contents?.lot;
      if (this.product.allowVariableStrength)
        this.bottling.strength = tank?.contents.proofed ?? 0;
    },
    async refresh() {
      if (this.bottlingId)
        return;
      this.allTanks = [];
      await this.$lazyListLoad(this.allTanks, 'tanks2', `id,name,site{id},contents{volume,alcoholType,alcoholName,alcoholId,proofed,lot}`, 100, [{field:'active', comparator:'eq', value:'true'}], [{name:'asof', value:this.bottling.date}]);
    },
    validate() {
      this.$nextTick(() => {
        this.$refs.form?.validate();
      });
    },
    bomCheck() {
      if (!this.edit || !this.bottling.completed)
        return true;
      if (!this.product || !this.product.bom)
        return false;

      // Must meet minimum requirements on product BOM
      const bottles = this.bottling.cases * this.product.caseSize + this.bottling.bottles;
      const cases = Math.floor(bottles / this.product.caseSize);
      for (const productItem of this.product.bom) {
        if (productItem.optional)
          continue;
        const quantityRequired = productItem.quantity * (productItem.per === 'Bottle' ? bottles : (productItem.per === 'Case' ? cases : 1));
        let quantity = quantityRequired;
        this.bottling.bom.forEach(bomItem => {
          if (bomItem.id === productItem.id)
            quantity -= bomItem.quantity;
        });
        if (quantity > 0) {
          const item = this.bomItem(productItem.id);
          return `A minimum ${quantityRequired} ${item.trackUnits} of ${item.name} required`;
        }
      }
      return true;
    },
    addItem() {
      const bomItem = {type:'Item',id:'',quantity:0,lot:'',uniqueId:Date.now()};
      this.changeBOMItem(bomItem, false);
      this.bottling.bom.push(bomItem);
      this.selectedItem = bomItem.uniqueId;
      this.validate();
    },
    deleteItem(index) {
      this.bottling.bom.splice(index, 1);
    },
    async changeBOMItem(bomItem, forceRefresh) {
      const item = this.bomItem(bomItem.id);
      if (!item.id)
        return;
//      if (forceRefresh || (item.lotTracking && !item.sites)) {
      if (forceRefresh || !item.sites) {
        const results = await this.$apiCall(`query ProductionBottlingEdit($id:ID!, $date:DateTime) {
                                                  item(id:$id) {
                                                    sites(asof:$date) {siteId,lot,quantity}
                                                  }
                                                }`,
                                            {id:item.id,date:this.bottling.date});
        if (!results.error)
          this.$set(item, 'sites', results.data.item.sites);
      }
      bomItem.lot = item.sites?.some(site => site.siteId === this.bottling.siteId && site.lot === bomItem.lot) ? bomItem.lot : '';

      const site = item.sites?.find(site => site.siteId === this.bottling.siteId);
      item.quantity = item.type === 'Item' ? `${site && !item.lotTracking ? this.$number(site.quantity, item.precision) : '0'} ${item.trackUnits}` : '';

      this.validate();
    },
    changeItemLot(bomItem) {
      const item = this.bomItem(bomItem.id);
      const site = item.sites?.find(site => site.siteId === this.bottling.siteId && site.lot === bomItem.lot);
      item.quantity = `${site ? this.$number(site.quantity, item.precision) : '0'} ${item.trackUnits}`;
    },
    currentLots(id) {
      const item = this.bomItem(id);
      return item && item.sites ?
                                item.sites.filter(site => site.siteId === this.bottling.siteId).map(site => site.lot)
                                : [];
    },

    async selectProduct() {
      if (this.bottling.productId && !this.product.bom) {
        const results = await this.$apiCall(`query ProductionBottlingEdit($id:ID!) {
                                              product(id:$id) {
                                                description,containerType,bom{type,id,quantity,per,optional}
                                              }
                                            }`,
                                            {id:this.product.id});
        if (!results.error) {
          this.product.description = results.data.product.description;
          this.product.bom = results.data.product.bom;
        }
      }
      if (!this.bottlingId)
        this.bottling.strength = this.product.strength || 0;
      this.setEstimate();
      this.bom();
    },
    bomItem(itemId) {
      return this.items.find(item => item.id === itemId) || {name:'MISSING', trackUnits:'', precision:0, lotTracking:false};
    },
    bom() {
      if (!this.product || !this.product.bom || !this.edit /*|| this.bottlingId*/)
        return;
      let uniqueId = Date.now();
      this.bottling.bom = this.product.bom.map(productItem => {
        const bottles = this.bottling.cases * this.product.caseSize + this.bottling.bottles;
        const cases = Math.floor(bottles / this.product.caseSize);
        const quantity = productItem.quantity * (productItem.per === 'Bottle' ? bottles : (productItem.per === 'Case' ? cases : 1));
        const lot = this.bottling.bom.find(item => item.id === productItem.id)?.lot;
        return {type:productItem.type,id:productItem.id,quantity,lot,uniqueId:uniqueId++};
      });
      for (const bomItem of this.bottling.bom)
        this.changeBOMItem(bomItem, false);
      this.validate();
    },
    async refreshLot() {
      if (this.bottlingId)    // Don't do for editing existing bottling
        return;
      this.loading = true;
      const results = await this.$apiCall(`query ProductionBottlingEdit($asof:DateTime!) {
                                            lotUnique(prefix:"P",date:$asof)
                                          }`,
                                          {asof:this.bottling.date});
      if (!results.error)
        this.bottling.lot = results.data.lotUnique;

      for (const bomItem of this.bottling.bom)
        await this.changeBOMItem(bomItem, true);

      this.loading = false;
    },
    cancel() {
      this.$router.back();
    },
    async save() {
      const incomplete = this.bottling.bom.some(item => !item.id ||
                                                        item.quantity < 0 ||
                                                        (this.bomItem(item.id).lotTracking && this.required(item.lot) !== true));
      if (!this.valid || incomplete) {
        this.$store.commit('setError', {type:'warning',message:`One or more fields is missing or invalid`});
        return;
      }
      this.saving = true;
      // eslint-disable-next-line no-unused-vars
      const {created, ...input} = this.bottling;
      input.bom = this.bottling.bom.map(item => {
        // eslint-disable-next-line no-unused-vars
        const {uniqueId, ...i} = item;
        return i;
      });
      const results = await this.$apiCall(`mutation ProductionBottlingEdit($input: BottlingInput!) {
                                             bottlingCreate(input:$input) {id}
                                           }`,
                                          {input})

      this.saving = false;
      if (!results.error) {
        this.dirty = false;
        this.$router.back();
        this.$store.commit('setError', {type:'success',message:`${this.bottling.id?'Updated':'Created'} bottling for ${this.product.name}`});
        if (this.activityId)
          this.$store.dispatch('checkTasks', true);
      }
    }
  }
}
</script>